import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, ChevronRight, BookOpen, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;

const empty = {
    nama: "", no_hp: "", no_rekening: "", nama_bank: "", nama_pemilik_rekening: "",
    no_id_ktp: "", alamat: "", status: "aktif",
};

export default function VendorPage() {
    const { canWrite, canDelete } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data } = await api.get("/vendors", { params: query ? { q: query } : {} });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { const t = setTimeout(() => { load(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
    const openEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); setDialogOpen(true); };

    const save = async () => {
        if (!form.nama?.trim()) return toast.error("Nama vendor wajib diisi");
        if (!form.no_hp?.trim()) return toast.error("No. HP wajib diisi");
        setSubmitting(true);
        try {
            if (editing) {
                await api.put(`/vendors/${editing.id}`, form);
                toast.success("Vendor berhasil diperbarui");
            } else {
                await api.post("/vendors", form);
                toast.success("Vendor berhasil ditambahkan");
            }
            setDialogOpen(false);
            load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            const { data } = await api.delete(`/vendors/${confirmDelete.id}`);
            const c = data?.cascade_cleaned || {};
            const n = Object.entries(c).filter(([k]) => k !== "share_tokens").reduce((s, [, v]) => s + (v || 0), 0);
            toast.success(n > 0
                ? `Vendor ${confirmDelete.nama} berhasil dihapus. ${n} transaksi lama di Riwayat Terhapus ikut dibersihkan.`
                : `Vendor ${confirmDelete.nama} berhasil dihapus.`);
            setConfirmDelete(null);
            load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div className="space-y-4 md:space-y-5">
            <PageHeader
                title="Master Vendor"
                description="Klik baris untuk lihat detail & transaksi vendor"
                actions={canWrite() && (
                    <Button onClick={openCreate} data-testid="vendor-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Tambah Vendor
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-3" style={{ border: "1px solid var(--border)" }}>
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <Input placeholder="Cari vendor..." value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid="vendor-search-input"
                        className="pl-9 rounded-lg h-10" style={{ background: "#fff", border: "1px solid var(--border)" }} />
                </div>
            </div>

            {/* MOBILE: card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={UsersIcon} title="Belum ada vendor" description="Tambahkan vendor pertama Anda." testid="vendor-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => (
                    <div
                        key={row.id}
                        className="rounded-xl bg-white p-4 cursor-pointer active:bg-[var(--bg-subtle)] transition-colors"
                        style={{ border: "1px solid var(--border)" }}
                        onClick={() => nav(`/vendor/${row.id}`)}
                        data-testid={`vendor-card-${row.id}`}
                    >
                        <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="font-mono text-xs font-semibold" style={{ color: "var(--apple-blue)" }}>{row.kode_vendor}</span>
                                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${row.status === "aktif" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                                        {row.status}
                                    </span>
                                </div>
                                <div className="font-medium text-sm truncate" style={{ color: "var(--text-primary)" }}>{row.nama}</div>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                                    {row.no_hp}{row.nama_bank ? ` · ${row.nama_bank}` : ""}
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-2" style={{ borderTop: "1px solid var(--border-soft)" }} onClick={(e) => e.stopPropagation()}>
                            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.created_at)}</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => nav(`/vendor/${row.id}`)} data-testid={`vendor-buka-buku-m-${row.id}`} className="p-2 rounded-lg" style={{ color: "var(--apple-blue)" }} title="Buka Buku">
                                    <BookOpen className="w-4 h-4" />
                                </button>
                                {canWrite() && (
                                    <button onClick={() => openEdit(row)} data-testid={`vendor-edit-m-${row.id}`} className="p-2 rounded-lg" style={{ color: "var(--text-secondary)" }} title="Edit">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                )}
                                {canDelete() && (
                                    <button onClick={() => setConfirmDelete(row)} data-testid={`vendor-delete-m-${row.id}`} className="p-2 rounded-lg" style={{ color: "var(--apple-red)" }} title="Hapus">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* DESKTOP: table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="vendor-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-24">Kode</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nama Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">No. HP</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bank</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dibuat</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 w-32">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={7}>
                                <EmptyState icon={UsersIcon} title="Belum ada vendor" description="Tambahkan vendor pertama Anda." testid="vendor-empty-desktop" />
                            </TableCell></TableRow>
                        ) : pageItems.map((row) => (
                            <TableRow key={row.id} data-testid={`vendor-row-${row.id}`}
                                className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors cursor-pointer"
                                onClick={() => nav(`/vendor/${row.id}`)}
                            >
                                <TableCell className="font-semibold text-teal-700">{row.kode_vendor}</TableCell>
                                <TableCell className="text-sm font-medium text-slate-900">
                                    <div className="flex items-center gap-2">
                                        {row.nama}
                                        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                                    </div>
                                </TableCell>
                                <TableCell className="text-sm text-slate-700">{row.no_hp}</TableCell>
                                <TableCell className="text-sm text-slate-700">{row.nama_bank || "-"}</TableCell>
                                <TableCell>
                                    <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${row.status === "aktif" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                                        {row.status}
                                    </span>
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">{formatTanggal(row.created_at)}</TableCell>
                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                        <button onClick={() => nav(`/vendor/${row.id}`)} data-testid={`vendor-buka-buku-${row.id}`} className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="Buka Buku Vendor">
                                            <BookOpen className="w-4 h-4" />
                                        </button>
                                        {canWrite() && (
                                            <button onClick={() => openEdit(row)} data-testid={`vendor-edit-btn-${row.id}`} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition-colors" title="Edit">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                        )}
                                        {canDelete() && (
                                            <button onClick={() => setConfirmDelete(row)} data-testid={`vendor-delete-btn-${row.id}`} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors" title="Hapus">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                        <div className="text-xs text-slate-500">
                            Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length} data
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs text-slate-600 px-2">Halaman {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Vendor" : "Tambah Vendor"}</DialogTitle>
                        <DialogDescription>{editing ? "Perbarui data vendor." : "Lengkapi data vendor baru."}</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                        <div className="sm:col-span-2">
                            <Label>Nama Vendor <span className="text-rose-600">*</span></Label>
                            <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="vendor-form-nama" className="mt-1.5 rounded-xl" placeholder="Contoh: Bengkel Jahit Sinar Mandiri" />
                        </div>
                        <div>
                            <Label>No. HP <span className="text-rose-600">*</span></Label>
                            <Input value={form.no_hp} onChange={(e) => setForm({ ...form, no_hp: e.target.value })} data-testid="vendor-form-nohp" className="mt-1.5 rounded-xl" placeholder="081234567890" />
                        </div>
                        <div>
                            <Label>Nama Bank</Label>
                            <Input value={form.nama_bank} onChange={(e) => setForm({ ...form, nama_bank: e.target.value })} className="mt-1.5 rounded-xl" placeholder="BCA / Mandiri" />
                        </div>
                        <div>
                            <Label>No. Rekening</Label>
                            <Input value={form.no_rekening} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Nama Pemilik Rekening <span className="text-xs text-slate-400 font-normal">— atas nama di buku rekening</span></Label>
                            <Input value={form.nama_pemilik_rekening} onChange={(e) => setForm({ ...form, nama_pemilik_rekening: e.target.value })} data-testid="vendor-form-pemilik-rekening" className="mt-1.5 rounded-xl" placeholder="Contoh: Wahyudi Setiawan" />
                        </div>
                        <div>
                            <Label>No. KTP</Label>
                            <Input value={form.no_id_ktp} onChange={(e) => setForm({ ...form, no_id_ktp: e.target.value })} className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Alamat</Label>
                            <Textarea value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                        <div>
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger data-testid="vendor-form-status" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="aktif">Aktif</SelectItem>
                                    <SelectItem value="nonaktif">Nonaktif</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="vendor-modal-save-button" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle data-testid="delete-vendor-title">Hapus Vendor {confirmDelete?.nama}?</AlertDialogTitle>
                        <AlertDialogDescription data-testid="delete-vendor-desc">
                            Semua transaksi vendor yang sudah di Riwayat Terhapus akan ikut dihapus <b>PERMANEN</b> dan tidak bisa dipulihkan. Vendor tidak boleh punya transaksi aktif.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" data-testid="cancel-delete-button">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menghapus...</> : "Hapus Permanen"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
